import React, { memo, useContext, useEffect, useRef, useState } from "react";
import defaultUserPic from "../../assets/img/user-profile.svg";
import ringingTone from "../../assets/sound/ringing.mp3"
import incomingTone from "../../assets/sound/incoming.mp3"
import { SocketContext } from "../../services/socketContext";
import * as chatService from "../../services/chatService";
import { ChatListDataContext, SignalRContext } from "../../layout/Baselayout";
import { useParams } from "react-router-dom";
import { getFormattedDateTime } from "../../utils";
import { useCall } from "../../services/callProviderContext";


const VoiceCalling = ({ user, closeModal, setCallingFlag }) => {
  const { localStream, remoteStream, incomingCall, setIncomingCall, acceptCall, endCall, callStatus, callReceived} = useCall();
  const users = JSON.parse(localStorage.getItem("users"));
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const { id } = useParams();
  const roomId = id?.split("_")[0];

  const { socket } = useContext(SocketContext);
  const [connection, onlineUsers] = useContext(SignalRContext);
  
  const remoteAudioRef = useRef(null);
  const localAudioRef = useRef(null);
  const audioRef = useRef(null);

  const [mute, setMute] = useState(false);
  const [callAccepted, setCallAccepted] = useState(false);
  const [callTimer, setCallTimer] = useState(0);

    useEffect(() => {
      if (!socket) return;
  
      const handleCallEnded = () => {
        closeModal();
      };
  
      socket.on('call-ended', handleCallEnded);
  
      return () => {
        socket.off('call-ended', handleCallEnded);
      };
    }, [socket]);

  // Handle audio streams
  useEffect(() => {
    
    if (remoteAudioRef.current && remoteStream) {
      remoteAudioRef.current.srcObject = remoteStream;
    }
    if (localAudioRef.current && localStream) {
      localAudioRef.current.srcObject = localStream;
      localAudioRef.current.muted = true; // Always mute local audio feed
    }
  }, [remoteStream, localStream]);

  const handleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      audioTrack.enabled = !audioTrack.enabled;
      setMute(!audioTrack.enabled);
    }
  };

  useEffect(() => {
    let timer;
    if (callReceived && remoteStream || callAccepted && remoteStream) {
      timer = setInterval(() => {
        setCallTimer((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [callReceived, callAccepted, remoteStream]);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

    // sending call logs
    const sendCallLog = async () => {
      const startDate = new Date(); // Current local time
      const endDate = new Date(startDate.getTime() + callTimer * 1000);
    
      const formatLocalDate = (date) => {
        const pad = (num) => num.toString().padStart(2, "0");
        return (date.getFullYear() +"-" +pad(date.getMonth() + 1) +"-" +pad(date.getDate()) +"T" +pad(date.getHours()) +":" +pad(date.getMinutes()) +":" +pad(date.getSeconds()) +"." +date.getMilliseconds().toString().padStart(3, "0"));
      };

      const callLogData={
        callerId: incomingCall ? incomingCall?.sender?.id : loginData.id ,
        receiverId: incomingCall ? loginData.id : user.id ? user.id : incomingCall?.sender?.id,
        currentUserId: incomingCall ? incomingCall?.sender?.id : loginData.id,
        startTime: (callReceived || callAccepted) ? formatLocalDate(startDate) : null ,
        endTime: (callReceived || callAccepted) ? formatLocalDate(endDate) : null
      }
      try {
          let response = await chatService.sendCallLog(callLogData);
          console.log(response);
      } catch (error) {
          console.error("Error sending call log:", error);
      }
    };
    
    // send call end message
    const sendCallEndMessage = async () => {
      try {
          const messageData = {
              senderId: incomingCall ? incomingCall?.sender?.id : loginData.id,
              receiverId: incomingCall ? loginData.id : user.id ? user.id : incomingCall?.sender?.id,
              roomId: incomingCall ? incomingCall?.reciever?.roomId : user.roomId ?? "" ,
              message: "Voice Call Ended",
              mediaUrl: "",
              senderName: incomingCall ? incomingCall?.caller : loginData?.userName,
          };
          
          await connection.invoke("SendMessage", messageData);
      } catch (error) {
          console.error("Error sending call end message:", error);
      }
  };

  const handleCallCancle = async() => {
    closeModal();

    const username = incomingCall ? incomingCall?.caller : user?.userName
    
    const socketId = users.find((item)=> item.username === username)?.socketId

    endCall(callStatus !== 'busy' ? socketId : null);
    setIncomingCall(null);

    await sendCallLog();
    await sendCallEndMessage();
    setCallingFlag(false);
  }

  // Auto reject after 30 seconds if call isn't accepted
  const autoRejectCall=async()=>{
    socket.emit('call-timeout', {
      from: socket.id,
    });
    setIncomingCall(null);
    await sendCallLog();
    await sendCallEndMessage();
  }

  useEffect(() => {
    // Cleanup any previous audio on status change
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    const timeoutId = setTimeout(() => {
      if (!callReceived || callStatus === 'busy') {
        autoRejectCall();
        // endCall();
        handleCallCancle();
        stopAudio();
      }
    }, 30000);

    const stopAudio = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  
    // Play incoming tone
    const tone = incomingCall ? incomingTone : ringingTone;
    audioRef.current = new Audio(tone);
    audioRef.current.loop = true;
    audioRef.current.play().catch(e => console.log("Incoming tone play error:", e));

    // Stop audio immediately if call is received/accepted
    if (callReceived || callAccepted) {
      stopAudio();
      clearTimeout(timeoutId);
    }

    return () => {
      clearTimeout(timeoutId);
      stopAudio();
    };
  
  }, [callStatus, callReceived, callAccepted, incomingCall]);

  return (
    <div className="calling-modal-content Create-new-group-popup">
      <div className="modal-header">
      <audio 
        ref={remoteAudioRef} 
        autoPlay 
        playsInline 
        className="remote-audio"
        // volume={speaker ? 1.0 : 0.5}
      />
      <audio 
        ref={localAudioRef} 
        autoPlay 
        playsInline 
        muted 
        className="local-audio"
      />
        <label>{"Calling"}</label>
        {/* <i
          className="fa fa-window-minimize text-white mr-3 cursor-pointer"
          onClick={handleMinimize}
        /> */}
        <i
          className="fa fa-times-circle cursor-pointer"
          aria-hidden="true"
          onClick={handleCallCancle}
        ></i>
      </div>

      <>
        <div className="modal-body text-center">
          <div className="calling-profile-wrapper mb-3">
            <img
              src={user?.profilePic || defaultUserPic}
              alt="Profile"
              className="calling-profile-pic"
            />
          </div>
          <h5 className="mb-2">{incomingCall ? incomingCall?.caller : user?.userName}</h5>
          <div className="text-muted">
            {(callReceived || callAccepted)
              ? formatTime(callTimer)
              : callStatus === "busy"
                ? "Calling..."
                : incomingCall
                  ? "Incoming"
                  : onlineUsers.includes(user?.id?.toString())
                    ? "Ringing..."
                    : "Calling..."}
          </div>
        </div>

          <div className="modal-footer justify-content-center">
            <button className="calling-icon-button" onClick={handleMute}>
              {mute ? (
                <i className="fa fa-microphone-slash" />
              ) : (
                <i className="fa fa-microphone" />
              )}
            </button>
            {incomingCall && !callAccepted && <button
              className="btn btn-success mx-2 calling-call-button"
              onClick={()=>{acceptCall();setCallAccepted(true)}}
              // disabled={callAccepted}
            >
              <i className="fa fa-phone" />
            </button>}
            <button className="btn btn-danger calling-end-button" onClick={handleCallCancle}>
              <i className="fa fa-phone" />
            </button>
          </div>
        </>
    </div>
  );
};

export default memo(VoiceCalling);
